import { useCallback, useEffect, useRef } from 'react'

import { Layout } from '@loadsmart/loadsmart-ui'
import { Header, Pagination, PageContent } from '@loadsmart/miranda-react'
import { type PaginationChangeEventDetails } from '@loadsmart/miranda-wc'

import { RequestStatus } from 'core/domain/Request'
import { AsyncContent } from 'core/ui/async-content/AsyncContent'
import { LoadingState } from 'core/ui/async-content/LoadingState'
import { useAbortController } from 'core/ui/hooks/useAbortController'
import { useSearchState } from 'core/ui/hooks/useSearchState'

import { analytics } from '../../core/utils/analytics'
import { BidState, type BidStateFilter } from '../domain/RfpSummary'
import { EmptyRfpHistoryState } from './EmptyRfpHistoryState'
import { ErrorState } from './ErrorState'
import { useProposalHistory } from './hooks/useProposalHistory'
import { RfpHistoryHeader } from './RfpHistoryHeader'
import { RfpSummaryList } from './RfpSummaryList'

const DEFAULT_PAGE = 1
const DEFAULT_PAGE_SIZE = 20

export type SearchFilters = {
  page?: number
  bidState?: BidStateFilter
}

export function PaginatedRfpHistoryPage() {
  const [searchParams, setSearchParams] = useSearchState<SearchFilters>()
  const { rfpHistory, rfpHistoryStatus, totalCount, fetchPaginatedRfpHistory } =
    useProposalHistory()
  const firstFilter = useRef(true)
  const firstFetch = useRef(true)
  const abortController = useAbortController([searchParams.bidState, searchParams.page])

  // Subtract one because miranda's pagination starts at 0
  const currentPage = Number(searchParams.page ?? DEFAULT_PAGE) - 1

  const onFilterChanged = useCallback(
    (bidState: BidStateFilter) => {
      setSearchParams({ bidState, page: 1 })
    },
    [setSearchParams]
  )

  const onPaginationChange = useCallback(
    ({ detail }: CustomEvent<PaginationChangeEventDetails>) => {
      // Sum one because miranda's pagination starts at 0
      setSearchParams(prev => ({ ...prev, page: detail.page + 1 }))
    },
    [setSearchParams]
  )

  useEffect(() => {
    if (firstFilter.current) {
      setSearchParams({ bidState: BidState.Open })

      firstFilter.current = false
    }
  }, [setSearchParams])

  useEffect(() => {
    const setFilterAll =
      firstFetch.current && rfpHistoryStatus === RequestStatus.Done && rfpHistory.length === 0

    if (setFilterAll) {
      setSearchParams({ bidState: 'all' })
      firstFetch.current = false
    }
  }, [rfpHistory, rfpHistoryStatus, setSearchParams])

  useEffect(() => {
    const bidState = searchParams.bidState ?? BidState.Open
    fetchPaginatedRfpHistory({ bidState, page: searchParams.page }, abortController)

    return () => {
      abortController.abort()
    }
  }, [abortController, searchParams.bidState, searchParams.page, fetchPaginatedRfpHistory])

  useEffect(() => {
    if (rfpHistoryStatus === RequestStatus.Done) {
      analytics.track('Loaded: Command Center', {
        number_of_entities: totalCount,
        page_name: 'RFPs',
      })
    }
  }, [rfpHistoryStatus, totalCount])

  return (
    <PageContent>
      <Header>
        <Header.Content>
          <Header.Title>RFPs</Header.Title>
        </Header.Content>
      </Header>
      <PageContent.Body>
        <Layout.Stack space="l">
          <RfpHistoryHeader
            rfpCount={totalCount ?? 0}
            onFilterChanged={onFilterChanged}
            filter={searchParams.bidState as BidState}
          />
          <AsyncContent
            status={rfpHistoryStatus}
            count={rfpHistory.length}
            loadingState={<LoadingState />}
            errorState={<ErrorState />}
            emptyState={<EmptyRfpHistoryState bidState={searchParams.bidState as BidState} />}
          >
            <Layout.Stack space="none" style={{ overflowX: 'auto' }}>
              <RfpSummaryList rfpHistory={rfpHistory} />
              <Pagination
                count={totalCount}
                page={currentPage}
                pageSize={DEFAULT_PAGE_SIZE}
                onChange={onPaginationChange}
              />
            </Layout.Stack>
          </AsyncContent>
        </Layout.Stack>
      </PageContent.Body>
    </PageContent>
  )
}
